import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getDeletedEmployees, restoreEmployee, getEmployeeDeletionLogs } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Trash2, RotateCcw, RefreshCw, History as HistoryIcon, Users, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const DeletedEmployees = ({ user, setUser }) => {
  const [loading, setLoading] = useState(false);
  const [deleted, setDeleted] = useState([]);
  const [logs, setLogs] = useState([]);
  const [restoreDialog, setRestoreDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([getDeletedEmployees(500), getEmployeeDeletionLogs(200)]);
      setDeleted(d.data.employees || []);
      setLogs(l.data.logs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitRestore = async () => {
    if (!restoreDialog) return;
    setSubmitting(true);
    try {
      await restoreEmployee(restoreDialog.id, reason.trim() || undefined);
      toast.success(`${restoreDialog.employee_id} restored`);
      setRestoreDialog(null);
      setReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="deleted-employees-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Trash2 className="h-6 w-6 text-red-600" />
              Deleted Employees
            </h1>
            <p className="text-sm text-slate-500">
              Soft-deleted employee records. Only <b>Admin</b> can restore them.
              Attendance, leaves and payroll history are preserved.
            </p>
          </div>
          <Button onClick={load} variant="outline" data-testid="refresh-btn" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="pt-5">
            <p className="text-xs text-slate-500">Currently Deleted</p>
            <p className="text-2xl font-bold text-red-700" data-testid="kpi-deleted">{deleted.length}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs text-slate-500">Total Log Entries</p>
            <p className="text-2xl font-bold text-slate-700">{logs.length}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs text-slate-500">Deletes (last 30d)</p>
            <p className="text-2xl font-bold text-orange-700">
              {logs.filter((l) => l.action === 'delete' && (Date.now() - new Date(l.created_at)) < 30 * 24 * 3600 * 1000).length}
            </p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs text-slate-500">Restores (last 30d)</p>
            <p className="text-2xl font-bold text-green-700">
              {logs.filter((l) => l.action === 'restore' && (Date.now() - new Date(l.created_at)) < 30 * 24 * 3600 * 1000).length}
            </p>
          </CardContent></Card>
        </div>

        <Tabs defaultValue="deleted">
          <TabsList>
            <TabsTrigger value="deleted" data-testid="tab-deleted">
              <Users className="h-4 w-4 mr-1" />Deleted ({deleted.length})
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">
              <HistoryIcon className="h-4 w-4 mr-1" />Audit Log ({logs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="deleted">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="deleted-table">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-xs font-medium">Employee ID</th>
                        <th className="text-left p-3 text-xs font-medium">Name</th>
                        <th className="text-left p-3 text-xs font-medium">Email / Phone</th>
                        <th className="text-left p-3 text-xs font-medium">Deleted On</th>
                        <th className="text-left p-3 text-xs font-medium">Deleted By</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {deleted.map((e) => (
                        <tr key={e.id} className="border-b hover:bg-slate-50" data-testid={`deleted-row-${e.id}`}>
                          <td className="p-3 font-mono text-indigo-700">{e.employee_id}</td>
                          <td className="p-3 font-medium">{e.name}</td>
                          <td className="p-3 text-xs text-slate-600">
                            <div>{e.email || '—'}</div>
                            <div className="text-slate-400">{e.phone || '—'}</div>
                          </td>
                          <td className="p-3 text-xs">{e.deleted_at ? new Date(e.deleted_at).toLocaleString() : '—'}</td>
                          <td className="p-3 text-xs">
                            <div>{e.deleted_by_user_name}</div>
                            <div className="text-[10px] uppercase text-slate-400">{e.deleted_by_user_role}</div>
                          </td>
                          <td className="p-3">
                            {isAdmin ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-green-500 text-green-700 hover:bg-green-50"
                                onClick={() => { setRestoreDialog(e); setReason(''); }}
                                data-testid={`restore-btn-${e.id}`}
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Restore
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400">Admin only</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {deleted.length === 0 && (
                        <tr><td colSpan="6" className="p-12 text-center text-slate-500">
                          {loading ? 'Loading...' : 'No deleted employees'}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="logs">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto max-h-[600px]">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-xs font-medium">When</th>
                        <th className="text-left p-3 text-xs font-medium">Action</th>
                        <th className="text-left p-3 text-xs font-medium">Employee</th>
                        <th className="text-left p-3 text-xs font-medium">By</th>
                        <th className="text-left p-3 text-xs font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id} className="border-b hover:bg-slate-50">
                          <td className="p-3 text-xs whitespace-nowrap">
                            {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                          </td>
                          <td className="p-3">
                            <span className={`px-1.5 py-0.5 text-[10px] uppercase rounded ${
                              log.action === 'delete' ? 'bg-red-100 text-red-700'
                              : log.action === 'restore' ? 'bg-green-100 text-green-700'
                              : 'bg-slate-800 text-white'
                            }`}>{log.action.replace('_', ' ')}</span>
                          </td>
                          <td className="p-3 text-xs">
                            <div className="font-medium">{log.employee_code}</div>
                            <div className="text-slate-500">{log.employee_name}</div>
                          </td>
                          <td className="p-3 text-xs">
                            <div>{log.performed_by_user_name}</div>
                            <div className="text-[10px] uppercase text-slate-400">{log.performed_by_user_role}</div>
                          </td>
                          <td className="p-3 text-xs italic text-slate-600">{log.reason || '—'}</td>
                        </tr>
                      ))}
                      {logs.length === 0 && (
                        <tr><td colSpan="5" className="p-12 text-center text-slate-500">No activity logged yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={!!restoreDialog} onOpenChange={(v) => !v && setRestoreDialog(null)}>
          <DialogContent className="max-w-md" data-testid="restore-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-green-600" />
                Restore Employee
              </DialogTitle>
              <DialogDescription>
                This will restore the employee to active status and re-enable their login.
              </DialogDescription>
            </DialogHeader>
            {restoreDialog && (
              <div className="space-y-3">
                <div className="p-3 bg-slate-50 border rounded">
                  <p className="font-mono text-indigo-800 font-semibold">{restoreDialog.employee_id}</p>
                  <p>{restoreDialog.name}</p>
                  <p className="text-xs text-slate-500">
                    Deleted {restoreDialog.deleted_at ? new Date(restoreDialog.deleted_at).toLocaleString() : ''} by {restoreDialog.deleted_by_user_name}
                  </p>
                </div>
                <div>
                  <Label>Reason (optional)</Label>
                  <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Deleted in error / rejoining after clearance"
                    data-testid="restore-reason"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setRestoreDialog(null)}>Cancel</Button>
              <Button onClick={submitRestore} disabled={submitting} className="bg-green-600 hover:bg-green-700" data-testid="confirm-restore">
                {submitting ? 'Restoring...' : 'Restore'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DeletedEmployees;
